import { Grid, GridCell, GridColumn} from '@progress/kendo-react-grid'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import * as GenericRedux from 'generic/genericRedux'
import { RasaReactComponent } from 'generic/rasaReactComponent'
import isNil from 'lodash/isNil'
import React from 'react'
import { Button, Row} from 'reactstrap'
import { subscribersFormatDate } from 'shared_server_client/dates'
import './styles.css'
import { Dataset } from 'generic/dataset'
import { isTruthy } from 'generic/utility'
import { BillingPlanDetailCode } from '../../shared_server_client/types/billing_plan';
import { STATUS_CODES } from '../../constants';
import { ERROR_STATUS } from '../subscriber-grid/constants';
import { ResubscribeReasonModal } from '../subscriber-grid/modals';
import { EditContactModal } from './modals'

const MAX_LOG_TO_SHOW = 10
type Subscriber = any

interface SubscriberIdProps {
  subscriberId: string,
}

interface SubscriberDetailsState {
  communityId: string,
  drop?: boolean
  editMode: boolean,
  isEditing: boolean,
  email: string,
  first_name: string,
  hasSegmentAccess?: boolean,
  isImportedSegment?: boolean,
  last_name: string,
  segment_code?: string,
  segments?: any[],
  subscriptionLog?: any[],
  activityHistoryCollapsed: boolean,
  lastActivityFeedCollapsed: boolean,
}

interface SubscriberDetailProps extends GenericRedux.AllComponentPropsWithModal<Subscriber> {
  communityTimeZone: string
  subscriberId: string
  onLoad: any
}

class SubscriberDetailComponent extends RasaReactComponent<SubscriberDetailProps, SubscriberDetailsState> {
  constructor(props: SubscriberDetailProps) {
    super(props, 'rasaSubscriber')

    this.state = {
      communityId: '',
      drop: false,
      editMode: false,
      isDirty: false,
      isLoading: true,
      isSaving: false,
      isEditing: false,
      email: '',
      first_name: '',
      hasSegmentAccess: false,
      isImportedSegment: false,
      last_name: '',
      segments: [],
      subscriptionLog: [],
      activityHistoryCollapsed: false,
      lastActivityFeedCollapsed: false,
    }
  }

  public componentDidMount() {
    this.loadOnRouting(this.props.subscriberId, '/contacts/detail')
  }

  public loadRecord(communityId: string, recordId: any): Promise<boolean> {
    this.setState({
      isLoading: true,
    })
    return super.loadRecord(communityId, recordId).then((result) => {
      this.props.onLoad(this.props.data)
      this.setState({
        segment_code: this.props.data.segment_code,
      })
      this.context.user.init().then(({ activeCommunity }) => {
        const avlFeatures: BillingPlanDetailCode[] = activeCommunity.billingInfo.currentPlan.features || []
        const hasSegmentAccess = avlFeatures.indexOf(BillingPlanDetailCode.SEGMENT_CODE) > -1
        this.setState({
          hasSegmentAccess,
        })
        if(hasSegmentAccess) {
          new Dataset().loadCommunityDataset('communityIntegrationFieldMappings', activeCommunity.communityId).then((communityFieldMappings) => {
            const isSegmentCodeExist = communityFieldMappings.find((x) => x.rasaFieldName === 'segment_code')
            if(isSegmentCodeExist){
              this.setState({
                isImportedSegment: true,
              })
            }
          })
        }
      })
      this.getSubscriptionLog()
      return result
    })
  }

  public render = () => {
    if (this.state.isLoading) {
      return (
        <Loading size="64" />
      )
    }

    if (!this.state.isLoading && !this.props.data.id) {
      return (
        <div className="subscriber-details-error">
          <h3>Contact not found</h3>
          <p>Unable to load contact details. Please try again.</p>
        </div>
      )
    }

    const statusBadgeClass = this.props.data.is_archived 
      ? 'status-badge archived' 
      : this.isSubscribed() 
        ? 'status-badge subscribed' 
        : 'status-badge unsubscribed'

    const statusText = this.props.data.is_archived
      ? 'Archived'
      : this.isSubscribed() ? 'Subscribed' : 'Unsubscribed'

    return (
      <div className="subscriber-details-modern">
        {/* Header Section */}
        <div className="contact-header">
          <div className="contact-info">
            <div className="contact-avatar">
              {(this.props.data.first_name && this.props.data.first_name[0] || this.props.data.email && this.props.data.email[0] || 'C').toUpperCase()}
            </div>
            <div className="contact-identity">
              <h1 className="contact-name">
                {`${this.props.data.first_name || ''} ${this.props.data.last_name || ''}`.trim() || 'Unknown Contact'}
              </h1>
              <p className="contact-email">{this.props.data.email || 'No email provided'}</p>
              <div className="contact-status">
                <span className={statusBadgeClass}>{statusText}</span>
                {this.lastActiveDate() && (
                  <span className="last-active">
                    Last active {subscribersFormatDate(this.lastActiveDate(), this.props.communityTimeZone)}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="contact-actions">
            <Button
              color="primary"
              outline
              onClick={this.initializeEdit}
              className="action-btn"
            >
              Edit Contact
            </Button>
            
            {!this.props.data.is_archived && (
              <Button
                color={this.isSubscribed() ? "warning" : "success"}
                outline
                onClick={this.isSubscribed() ? 
                  () => {
                    this.propertyChanged('is_subscribed', 0)
                    this.propertyChanged('unsubscribe_reason', 'UI')
                    this.saveRecord(this.props.data.id)
                  } : 
                  this.resubscribe
                }
                className="action-btn"
              >
                {this.isSubscribed() ? 'Unsubscribe' : 'Subscribe'}
              </Button>
            )}
            
            <Button
              color={this.props.data.is_archived ? "info" : "danger"}
              outline
              onClick={() => {
                this.propertyChanged('is_archived', this.props.data.is_archived ? 0 : 1)
                this.saveRecord(this.props.data.id)
              }}
              className="action-btn"
            >
              {this.props.data.is_archived ? 'Restore' : 'Archive'}
            </Button>
          </div>
        </div>

        {/* Details Grid */}
        <div className="contact-details-grid">
          {/* Contact Information */}
          <div className="details-card">
            <h3 className="card-title">Contact Information</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Email</label>
                <span>{this.props.data.email || '—'}</span>
              </div>
              <div className="detail-item">
                <label>First Name</label>
                <span>{this.props.data.first_name || '—'}</span>
              </div>
              <div className="detail-item">
                <label>Last Name</label>
                <span>{this.props.data.last_name || '—'}</span>
              </div>
              {this.state.hasSegmentAccess && (this.state.segments.length > 0 || this.props.data.segment_code) && (
                <div className="detail-item">
                  <label>Segment</label>
                  <span>{this.props.data.segment_code && this.props.data.segment_code !== '-1' ? this.props.data.segment_code : '—'}</span>
                </div>
              )}
              {this.props.data.unsubscribe_reason && (
                <div className="detail-item">
                  <label>Unsubscribe Reason</label>
                  <span 
                    className={`unsubscribe-reason ${this.props.data.unsubscribe_reason && this.props.data.unsubscribe_reason.toLowerCase() === 'dropped' && this.props.data.dropped_from_bounce ? 'has-bounce-info' : ''}`}
                    title={this.getBounceTooltipText()}
                  >
                    {this.props.data.unsubscribe_reason}
                  </span>
                </div>
              )}
              <div className="detail-item">
                <label>Created</label>
                <span>{subscribersFormatDate(this.props.data.created, this.props.communityTimeZone) || '—'}</span>
              </div>
              <div className="detail-item">
                <label>Last Updated</label>
                <span>{subscribersFormatDate(this.props.data.updated, this.props.communityTimeZone) || '—'}</span>
              </div>
              <div className="detail-item">
                <label>Source</label>
                <span>{this.props.data.data_source_name || '—'}</span>
              </div>
              <div className="detail-item">
                <label>External ID</label>
                <span>{this.props.data.external_id || '—'}</span>
              </div>
            </div>
          </div>

          {/* Engagement Metrics */}
          <div className="details-card">
            <h3 className="card-title">Engagement Metrics</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Last Newsletter</label>
                <span>{subscribersFormatDate(this.props.data.last_newsbrief, this.props.communityTimeZone) || '—'}</span>
              </div>
              <div className="detail-item">
                <label>Last Clicked</label>
                <span>{subscribersFormatDate(this.props.data.last_click, this.props.communityTimeZone) || '—'}</span>
              </div>
              <div className="detail-item">
                <label>Last Opened</label>
                <span>{subscribersFormatDate(this.props.data.last_open, this.props.communityTimeZone) || '—'}</span>
              </div>
              <div className="detail-item">
                <label>Unique Clicks</label>
                <span className="metric-value">{this.props.data.unique_clicks || 0}</span>
              </div>
              <div className="detail-item">
                <label>Unique Opens</label>
                <span className="metric-value">{this.props.data.unique_opens || 0}</span>
              </div>
            </div>
          </div>

          {/* Activity History */}
          {this.state.subscriptionLog.length > 0 && (
            <div className="details-card full-width collapsible-section">
              <h3 
                className="card-title collapsible-header"
                onClick={() => this.setState({activityHistoryCollapsed: !this.state.activityHistoryCollapsed})}
              >
                Activity History
                <div className="collapse-button">
                  <span className={`collapse-icon ${this.state.activityHistoryCollapsed ? 'collapsed' : 'expanded'}`}>▲</span>
                </div>
              </h3>
              <div className={`collapsible-content ${this.state.activityHistoryCollapsed ? 'collapsed' : 'expanded'}`}>
                {!this.state.activityHistoryCollapsed && (
                  <div className="activity-timeline">
                    {this.state.subscriptionLog.slice(0, MAX_LOG_TO_SHOW).map((log, index) => {
                      const changes = JSON.parse(log.change)
                      return (
                        <div key={index} className="timeline-item">
                          <div className="timeline-marker"></div>
                          <div className="timeline-content">
                            <span className="activity-action">{this.getChangeAction(changes)}</span>
                            <span className="activity-date">{subscribersFormatDate(log.created, this.props.communityTimeZone)}</span>
                            <span className="activity-source">via {log.source}</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <ResubscribeReasonModal
          isSaving={this.state.isSaving}
          onConfirm={this.handleResubscribeReasonModalConfirm}
          data={this.props.modals}
          closeModal={this.props.closeModal}
          title={"Reason for removal"}
        />
        {!this.props.data.is_archived &&
          <EditContactModal
            data={this.props.modals}
            updateModal={this.props.updateModal}
            closeModal={this.props.closeModal}
            saveModal={this.props.saveModal}
            onSave={this.handleUpdateContact}
          />
        }
      </div>
    )
  }

  private getChangeAction = (changes) => {
    if (Object.keys(changes).indexOf('is_subscribed') > -1) {
      return isTruthy(changes.is_subscribed) ? 'Subscribed' : 'Unsubscribed'
    } else if (Object.keys(changes).indexOf('is_archived') > -1) {
      return isTruthy(changes.is_archived) ? 'Archived' : 'Restored'
    } else {
      return 'Updated'
    }
  }

  private getSubscriptionLog = () => {
    if (this.state.communityId && this.props.data.id) {
      return new Dataset().loadCommunityDataset('identityCommunityPersonLog', this.state.communityId,
        [{param: 'icpGuid', value: this.props.data.id}]).then((subscriptionLogDS) => {
          if (subscriptionLogDS[0].length)
            this.setState({
              subscriptionLog: subscriptionLogDS[0],
            })
        })
    }
  }

  private isSubscribed = (): boolean => {
    return this.props.data.hasOwnProperty('is_active')
      ? !!this.props.data.is_subscribed && !!this.props.data.is_active
      : !!this.props.data.is_subscribed
  }

  private lastActiveDate = () => {
    const lastOpen = this.props.data.last_open
    const lastClick = this.props.data.last_click
    if (lastOpen && lastClick) {
      return lastOpen > lastClick ? lastOpen : lastClick
    } else {
      return lastOpen || lastClick || ''
    }
  }

  private getBounceTooltipText = (): string => {
    if (this.props.data.unsubscribe_reason && this.props.data.unsubscribe_reason.toLowerCase() === 'dropped' && this.props.data.dropped_from_bounce) {
      const communityText = this.props.data.dropped_community 
        ? ` in newsletter "${this.props.data.dropped_community}"` 
        : ''
      return `This contact was Dropped due to bounce${communityText}`
    }
    return ''
  }

  private initializeEdit = () => {
    this.props.openModal(EditContactModal.key, {
      ...this.props.data,
      hasSegmentAccess: this.state.hasSegmentAccess,
      isImportedSegment: this.state.isImportedSegment
    })
  }

  private resubscribe = () => {
    this.propertiesChanged({
      is_force_resubscribe: 0,
      is_subscribed: 1,
      is_active: 1,
    })
    this.saveRecord(this.props.data.id)
      .catch((err) => {
        this.setState({
          isSaving: false
        })
        if(err.response && err.response.error && err.response.error.statusCode === STATUS_CODES.UNPROCESSABLE) {
          const body: {results: [{error: string, status_code: number}]} = JSON.parse(err.response.error.body)
          if(body.results.some(x => x.error === ERROR_STATUS.MultipleHardBounce)){
            this.props.openModal(ResubscribeReasonModal.key, {
              id: this.props.data.id,
              is_subscribed: true,
              is_force_resubscribe: true
            })
          }
        }
    })
  }

  private handleResubscribeReasonModalConfirm = (data: { id: string }, reason: string) => {
    this.propertiesChanged({
      is_subscribed: 1,
      is_force_resubscribe: true,
      resubscribe_reason: reason,
      is_active: 1,
    })
    this.saveRecord(data.id).then(() => {
      this.props.closeModal(ResubscribeReasonModal.key)
    })
  }

  private handleUpdateContact = (updatedData: { email: string; firstName: string; lastName: string, segmentCode: string }) => {
    this.propertiesChanged({
      email: updatedData.email,
      first_name: updatedData.firstName,
      last_name: updatedData.lastName,
      segment_code: updatedData.segmentCode
    })

    return this.saveRecord(this.props.data.id)
      .then(() => {
        this.props.closeModal(EditContactModal.key);

        this.setState({
          isSaving: false,
        })
      })
      .catch(() => {
        this.setState({
          isSaving: false,
        })
      })
  }
}

const SubscriberDetails: any = GenericRedux.registerNewNestedComponent(
  SubscriberDetailComponent,
  'subscriber_details')

interface Action {
  action_type: string
  created: string
  email_subject?: string
  id: string
  issue_date: string
  title?: string
  url?: string
  useragent?: string
}

interface Topic {
  topic: string
  first_click: string
  last_click: string
}

interface Send {
  status: string
  send_at: string
  email_subject?: string
  newsletter_title?: string
  id: string
}

interface Insights {
  actions: Action[],
  topics: Topic[],
  sends: Send[]
}

type SubscriberInsightsProps = SubscriberIdProps & GenericRedux.AllComponentProps<Insights> & SubscriberDetailProps

class SubscriberInsightsComponent extends RasaReactComponent<SubscriberInsightsProps> {
  constructor(props) {
    super(props, 'rasaSubscriberInsights')

    this.state = {
      ...this.state,
      lastActivityFeedCollapsed: false,
    }

    this.normalizeActionsData = this.normalizeActionsData.bind(this)
    this.normalizeTopicsData = this.normalizeTopicsData.bind(this)
    this.normalizeSendsData = this.normalizeSendsData.bind(this)
  }

  public componentDidMount() {
    this.loadOnRouting(this.props.subscriberId, '/contacts/detail')
  }

  public render = () => {
    return <div className="last-activity-feed collapsible-section">
      <h6 
        className="collapsible-header"
        onClick={() => this.setState({lastActivityFeedCollapsed: !this.state.lastActivityFeedCollapsed})}
      >
        Last Activity Feed
        <div className="collapse-button">
          <span className={`collapse-icon ${this.state.lastActivityFeedCollapsed ? 'collapsed' : 'expanded'}`}>▲</span>
        </div>
      </h6>
      <div className={`collapsible-content ${this.state.lastActivityFeedCollapsed ? 'collapsed' : 'expanded'}`}>
        {!this.state.lastActivityFeedCollapsed && (
          this.state.isLoading ?
            <div className="loader-wrapper">
              <div className="balls">
                <Loading size="64" />
              </div>
            </div>
          :
          <div className="grids-container">
            <div className="rasa-data-grid">
              <h4>Recent Actions</h4>
              <Grid
                data={this.normalizeActionsData(this.props.data.actions || [], this.props.communityTimeZone).slice(0, 15)}>
                <GridColumn field="action_type" title="Type" width={80}/>
                <GridColumn field="issue_date" title="Newsletter" width={120}/>
                <GridColumn field="created" title="Action Date" width={120}/>
                <GridColumn cell={ActivityTextCell} title="Title/Subject" width={200}/>
              </Grid>
            </div>
            <div className="rasa-data-grid">
              <h4>Topics Clicked</h4>
              <Grid data={this.normalizeTopicsData(
                  this.props.data.topics || [],
                  this.props.communityTimeZone).slice(0, 10)}>
                <GridColumn field="topic" title="Topic" width={120}/>
                <GridColumn field="last_click" title="Last Click" width={120}/>
                <GridColumn field="first_click" title="First Click" width={120}/>
              </Grid>
            </div>
            <div className="rasa-data-grid">
              <h4>Recent Sends</h4>
              <Grid data={this.normalizeSendsData(
                  this.props.data.sends || [],
                  this.props.communityTimeZone).slice(0, 10)}>
                <GridColumn field="status" title="Status" width={80}/>
                <GridColumn field="send_at" title="Send Date" width={120}/>
                <GridColumn field="subject" title="Title/Subject" width={180}/>
              </Grid>
            </div>
          </div>
        )}
      </div>
    </div>
  }

  private normalizeActionsData = (actions: Action[] = [], timezone: string) => {
    return actions.map( (action: Action) => {
      return {
        action_type: action.action_type,
        issue_date: new Date(action.issue_date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          weekday: 'short',
        }),
        created: subscribersFormatDate(action.created, timezone),
        email_subject: action.email_subject.split('|')[0],
        title: action.title,
        url: action.url,
      }
    })
  }

  private normalizeTopicsData = (topics: Topic[] = [], timezone: string) => {
    return topics.map((t: Topic) => {
      return {
        topic: t.topic,
        first_click: subscribersFormatDate(t.first_click, timezone),
        last_click: subscribersFormatDate(t.last_click, timezone),
      };
    })
  }

  private normalizeSendsData = (sends: Send[] = [], timezone: string) => {
    return sends.map((send: Send) => {
      return {
        status: send.status,
        send_at: subscribersFormatDate(send.send_at, timezone),
        subject: send.email_subject || send.newsletter_title || 'No Subject',
        id: send.id,
      };
    })
  }

}

class ActivityTextCell extends GridCell {
  public render() {
    return <td>
      {this.props.dataItem.action_type === 'open' ?
        this.props.dataItem.email_subject :
          <div className="post-title">
            <a id={`Title-${this.props.dataItem.url}`}
              href={this.props.dataItem.url}
              target="_blank" rel="noopener noreferrer">
              {this.props.dataItem.title || this.props.dataItem.url}
            </a>
          </div>
        }
    </td>
  }
}

const SubscriberInsights: any = GenericRedux.registerNewNestedComponent(
  SubscriberInsightsComponent,
  'subscriber_insights')

interface SubscriberDetailState {
  communityTimeZone: string,
  isLoaded: boolean,
  isSubscribed: boolean,
}

type SubscriberDetailsProps = GenericRedux.AllComponentPropsWithModal<any>

export class SubscriberDetailsComponent extends React.Component<SubscriberDetailsProps, SubscriberDetailState> {
  public static contextType = RasaContext

  constructor(props: SubscriberDetailsProps) {
    super(props, '')
    this.state = {
      communityTimeZone: '',
      isLoaded: false,
      isSubscribed: false,
    }
  }

  public componentDidMount() {
    this.context.user.init().then(({ activeCommunity }) => {
      this.setState({communityTimeZone: activeCommunity.data.company_time_zone || 'US/Central'});
    })
  }

  public render() {
    const guid: string = this.context.store.getState().app.params.id

    return isNil(guid) ? (
      <div></div>
    ) : (
      <div className="subscriber-details">
        <Row>
          <SubscriberDetails
            {...this.props}
            communityTimeZone={this.state.communityTimeZone}
            subscriberId={guid}
            onLoad={(s) => {
              this.setState({
                isLoaded: true,
                isSubscribed: s.is_subscribed,
              })
            }}
          />
        </Row>
        {(this.state.isLoaded && this.state.isSubscribed)
        ? <Row>
            <SubscriberInsights
              communityTimeZone={this.state.communityTimeZone}
              subscriberId={guid}
            />
          </Row>
        : <div></div>
        }
        {/* <Row>
          <div className="day-activity-feed">
            <h6>
              Time of the Day Activity:
            </h6>
          </div>
        </Row> */}
      </div>
    )
  }
}

export const SubscriberDetailsContainer = GenericRedux.registerNewComponentWithModals<any>(
  SubscriberDetailsComponent,
  'subscriber_detail_container',
  [ResubscribeReasonModal.key, EditContactModal.key]
)
